﻿import { useEffect, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import layout from "./dictionaries/layout.js";
import { animated } from '@react-spring/three';
import * as THREE from 'three';

import GameCamera from "./sceneSetUp/GameCamera.jsx";
import DisconnectModal from "./components/DisconnectModal.jsx";

// server
import { getSocket } from "./socket.js";

const socket = getSocket();
import { 
  blueMoonBrightnessAtom,
  clientAtom,
  deviceAtom, 
  isOnlineAtom,
} from "./GlobalState.jsx";
import useResponsiveSetting from "./hooks/useResponsiveSetting.jsx";
import useSoundEffectsPlayer from "./soundPlayers/useSoundEffectsPlayer.jsx";
import MeteorsRealShader from "./shader/meteorsReal/MeteorsRealShader.jsx";
import { trackEvent } from './api';
import MilkyWayNew from "./shader/milkyway/MilkyWayNew.jsx";

import FirstSectionNew from "./components/lobby/FirstSectionNew.jsx";
import SecondSection from "./components/lobby/SecondSection.jsx";
import ThirdSection from "./components/lobby/ThirdSection.jsx";
import TopSection from "./components/lobby/TopSection.jsx";
import BodySection from "./components/lobby/BodySection.jsx";
import ActionSection from "./components/lobby/ActionSection.jsx";
import BackToMainMenu from "./components/lobby/BackToMainMenu.jsx";
import { useParams } from "wouter";

export default function LobbyNew() {

  useResponsiveSetting();
  const device = useAtomValue(deviceAtom)
  const isOnline = useAtomValue(isOnlineAtom)
  const client = useAtomValue(clientAtom)
  const setBlueMoonBrightness = useSetAtom(blueMoonBrightnessAtom)
  const { playSoundEffect } = useSoundEffectsPlayer()
  const params = useParams()
  const countdownTimeouts = useRef([])

  useEffect(() => {
    trackEvent({ surface: 'lobby', action: 'page_view', category: 'page_view' });
  }, [])

  useEffect(() => {
    function playGameStartCountdown({ hostId }) {
      if (countdownTimeouts.current.length > 0) return
      playSoundEffect('/sounds/effects/set-dul-hana.mp3', 1)
      setBlueMoonBrightness(1.2)
      countdownTimeouts.current = [
        window.setTimeout(() => setBlueMoonBrightness(1.2), 1100),
        window.setTimeout(() => setBlueMoonBrightness(1.2), 2200),
        window.setTimeout(() => {
          setBlueMoonBrightness(null)
          countdownTimeouts.current = []
          if (client._id === hostId && params.id) {
            socket.emit('gameStart', { roomId: params.id.toUpperCase(), clientId: client._id })
          }
        }, 3300)
      ]
    }

    socket.on('gameStartCountdown', playGameStartCountdown)
    return () => {
      socket.off('gameStartCountdown', playGameStartCountdown)
      for (const timeoutId of countdownTimeouts.current) window.clearTimeout(timeoutId)
      countdownTimeouts.current = []
      setBlueMoonBrightness(null)
    }
  }, [client._id, params.id, playSoundEffect, setBlueMoonBrightness])

  const meteorShaderColor = new THREE.Color();
  meteorShaderColor.setHSL(0.05, 0.7, 0.4)
  return <animated.group>
    <GameCamera position={layout[device].camera.position}/>
    { device === 'landscapeDesktop' && <group>
      <FirstSectionNew position={[-9, 0, 0]} />
      <SecondSection position={[0.5, 0, 0]}/>
      <ThirdSection position={[0, 0, 0]}/>
    </group> }
    { device === 'portrait' && <group>
      <TopSection position={[0, 0, -10]}/>
      <BodySection position={[0, 0, 0]}/>
      <ActionSection position={[0, 0, 7]}/>
    </group>}
    <BackToMainMenu
      confirmationModalPosition={layout[device].lobby.confirmationModal.position}
      confirmationModalScale={layout[device].lobby.confirmationModal.scale}
      roomClosedModalPosition={layout[device].lobby.roomClosedModal.position}
      roomClosedModalScale={layout[device].lobby.roomClosedModal.scale}
    />
    { (!socket.connected || !isOnline) && <DisconnectModal
      position={layout[device].lobby.disconnectModal.position}
      rotation={layout[device].lobby.disconnectModal.rotation}
    /> }
    <MeteorsRealShader color={meteorShaderColor}/>
    <MilkyWayNew // will not show without a camera
      rotation={[-Math.PI/2, 0, -35.0]} 
      position={[0, -10, -4]}
      scale={5}
      brightness={0.5}
      colorTint1={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
      colorTint2={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
      colorTint3={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
    />
  </animated.group>
}

